// src/services/videoService.js
//import axios from 'axios';
import proxyapi from './ProxyApi';

/**
 * Service for handling video-related operations
 */

// Export patterns for reuse
export const VIDEO_PATTERNS = {
  youtube: /^(https?:\/\/(?:www\.|m\.)?)?(youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})(\S*)?$/i,
  vimeo:   /^(https?:\/\/)?(?:www\.|player\.)?vimeo\.com\/(?:video\/)?(\d+)(?:$|\/|\?)/i,
  dailym:  /^(https?:\/\/)?(?:www\.)?(?:dailymotion\.com\/video\/|dai\.ly\/)([a-zA-Z0-9]+)(?:$|\/|\?)/i
};

/**
 * Validate a video URL to ensure it's from an allowed platform
 * 
 * @param {string} url - Video URL to validate
 * @returns {Object|null} - Video data if valid, null otherwise
 */
export const validateVideoUrl = (url) => {
  if (!/^https?:\/\//i.test(url)) return null; // only http/https
  
  let m;
  if ((m = url.match(VIDEO_PATTERNS.youtube))) {
    const id = m[3];
    return {
      platform: "youtube",
      embedUrl: `https://www.youtube.com/embed/${id}`
    };
  }
  
  if ((m = url.match(VIDEO_PATTERNS.vimeo))) {
    const id = m[2];
    return {
      platform: "vimeo",
      embedUrl: `https://player.vimeo.com/video/${id}`
    };
  }
  
  if ((m = url.match(VIDEO_PATTERNS.dailym))) {
    const id = m[2];
    return {
      platform: "dailymotion",
      embedUrl: `https://www.dailymotion.com/embed/video/${id}`
    };
  }
  
  return null;
};

/**
 * Save video mappings (URLs) to the backend
 * 
 * @param {string} viewId - The view ID
 * @param {string} versionId - The version ID
 * @param {Object} uploadedVideos - Object mapping placeholder IDs to video URL objects
 * @param {string} userId - Current user ID
 * @returns {Promise<Object>} - The response with updated HTML
 */
export const saveVideoMappings = async (viewId, versionId, uploadedVideos, userId) => {
  try {
    // Create a flattened version of the video mappings
    const videoMappings = Object.entries(uploadedVideos).reduce((acc, [id, data]) => {
      if (!data) return acc;
      
      // Include all necessary video data
      acc[id] = {
        embedUrl: data.embedUrl,
        platform: data.platform
      };
      
      return acc;
    }, {});

    // Send the mappings to the backend
    const response = await proxyapi.put(
      `/api/v1/views/${viewId}/versions/${versionId}/videos`,
      { 
        userId,
        videoMappings
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error saving video mappings:', error);
    throw error;
  }
};

/**
 * Update HTML with video embeds for preview
 * This is a frontend-only function for immediate visual feedback
 * 
 * @param {string} html - Original HTML content 
 * @param {Object} uploadedVideos - Object mapping video IDs to video data
 * @returns {string} - Updated HTML with embedded videos
 */
// src/services/videoService.js
export const updateHtmlWithVideos = (html, uploadedVideos) => {
  if (!uploadedVideos || !html) return html;

  try {
    // Parse the HTML as a DOM
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // --- 1. Update static HTML placeholders as before ---
    for (const [videoId, videoData] of Object.entries(uploadedVideos)) {
      if (!videoData?.embedUrl) continue;
      const placeholder = doc.getElementById(videoId);
      if (!placeholder) continue;

      // Set placeholder's data-number attribute for badge display
      const match = videoId.match(/[0-9]+/);
      if (match) {
        placeholder.setAttribute('data-number', match[0]);
      }

      // Store original classes to preserve them
      const originalClasses = Array.from(placeholder.classList);

      // Remove any height classes as we'll use aspect-ratio instead
      const filteredClasses = originalClasses.filter(c => !/^h-\d/.test(c));
      placeholder.className = filteredClasses.join(' ');

      // Add required responsive classes if they don't exist
      if (!placeholder.classList.contains('relative')) placeholder.classList.add('relative');
      if (!placeholder.classList.contains('overflow-hidden')) placeholder.classList.add('overflow-hidden');
      if (!placeholder.classList.contains('w-full')) placeholder.classList.add('w-full');
      if (!placeholder.classList.contains('aspect-video')) placeholder.classList.add('aspect-video');

      // Clear existing content
      placeholder.innerHTML = '';

      // Create wrapper + iframe
      const wrapper = doc.createElement('div');
      wrapper.className = 'relative w-full h-full';
      const iframe = doc.createElement('iframe');
      iframe.src = videoData.embedUrl;
      iframe.className = 'absolute inset-0 w-full h-full';
      iframe.setAttribute('frameborder', '0');
      iframe.setAttribute('allowfullscreen', 'true');
      iframe.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture');
      wrapper.appendChild(iframe);
      placeholder.appendChild(wrapper);
    }

    // --- 2. Update video placeholders inside <script> tags (dynamic injection) ---
    const scripts = doc.querySelectorAll('script');
    scripts.forEach(script => {
      let scriptCode = script.textContent;
      let scriptCodeUpdated = scriptCode;

      for (const [videoId, videoData] of Object.entries(uploadedVideos)) {
        if (!videoData?.embedUrl) continue;
        // Regex to match <div ... id="video-X" ...>...</div>
        const divRegex = new RegExp(
          `<div([^>]*?)id=["']${videoId}["']([^>]*?)>([\\s\\S]*?)</div>`,
          'g'
        );
        scriptCodeUpdated = scriptCodeUpdated.replace(divRegex, (match, beforeId, afterId, inner) => {
          // Add data-number attribute for badge overlays
          const matchNum = videoId.match(/[0-9]+/);
          const dataNumber = matchNum ? ` data-number="${matchNum[0]}"` : '';
          // Use the same responsive classes as above
          const divClasses = 'relative overflow-hidden w-full aspect-video';
          const iframeTag = `<iframe src="${videoData.embedUrl}" class="absolute inset-0 w-full h-full" frameborder="0" allowfullscreen="true" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"></iframe>`;
          return `<div${beforeId}id="${videoId}"${afterId} class="${divClasses}"${dataNumber}><div class="relative w-full h-full">${iframeTag}</div></div>`;
        });
      }

      // Only update script content if it changed
      if (scriptCode !== scriptCodeUpdated) {
        script.textContent = scriptCodeUpdated;
      }
    });

    // Return the updated HTML
    return doc.documentElement.outerHTML;
  } catch (error) {
    console.error('Error updating HTML with videos:', error);
    return html; // Return original HTML on error
  }
};